import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Modal, Blockquote, List, ListItem } from "@wfp/ui";

import useStore from "helpers/useStore";
import { CommodityStatus, getDetailedStockStatus } from "helpers/stock";
import {
  deliveryCategory,
  getModelName,
  purchaseDetailCategory,
  studentAttendanceCategory,
  takeHomeRationCategory,
} from "SCConstants";
import {
  getAllStores,
  isDeliveryStore,
  isIncidentStore,
  isPurchaseDetailStore,
  isSchoolYearStore,
  Store,
} from "data-handler/ducks/stores";
import { useSelector } from "react-redux";
import moment from "moment";

import styles from "./styles.module.scss";

export type DeleteModalSection = "attendance" | "consumption" | undefined;

type DeleteModalStateProps = {
  state: boolean;
  setState: (state: boolean) => void;
  currentStoreData: Store;
  section: DeleteModalSection;
  customRedirectPath?: string;
};

const DeleteModal: React.ElementType<DeleteModalStateProps> = ({
  state,
  setState,
  currentStoreData,
  section,
  customRedirectPath = undefined,
}) => {
  const { updateStore, resetStore }: any = useStore();

  const intl = useIntl();

  const id =
    currentStoreData && currentStoreData.client_id
      ? currentStoreData.client_id
      : undefined;

  const allStores = useSelector(getAllStores);

  const schoolYear = isSchoolYearStore(currentStoreData)
    ? currentStoreData
    : undefined;

  // Unsynced carry-over incidents and initial stock deliveries/purchases
  // recorded for this school year's start date; they only exist because of
  // the school year, so they are dropped with it
  const unsyncedYearStockStores = schoolYear
    ? allStores.filter((store) => {
        if (store.sync !== false || store.id) return false;
        const isCarryOverIncident =
          isIncidentStore(store) &&
          store.meta?.is_initial_stock_incident === true;
        const isInitialStock =
          (isDeliveryStore(store) || isPurchaseDetailStore(store)) &&
          (store as any).meta?.is_initial_stock === true;
        return (
          (isCarryOverIncident || isInitialStock) &&
          moment((store as any).occurred_at).isSame(schoolYear.starts_on, "day")
        );
      })
    : [];

  const onSubmit = () => {
    // Flag for partially deleting on-site attendance objects if only
    // deleting one section (when both levels and consumption are in the object)
    const onSiteAttendancePartialDeletion =
      currentStoreData.model === "attendance" &&
      currentStoreData.category !== takeHomeRationCategory &&
      currentStoreData.category !== studentAttendanceCategory &&
      currentStoreData.levels &&
      currentStoreData.consumption &&
      section !== undefined;

    const wasBlocked =
      currentStoreData.sync === false &&
      !currentStoreData.id &&
      !onSiteAttendancePartialDeletion
        ? resetStore(id, customRedirectPath)
        : updateStore(
            {
              id: id,
              values: currentStoreData,
              model: currentStoreData.model,
              type: "delete",
              section,
              category: currentStoreData.category,
            },
            customRedirectPath
          );

    // Only close modal if action wasn't blocked
    if (!wasBlocked) {
      unsyncedYearStockStores.forEach((store) => resetStore(store.client_id));
      setState(false);
    }
  };

  const stockStatus = useSelector(
    getDetailedStockStatus(moment().format("YYYY-MM-DD"))
  );

  const getUndeletableStoreCommodities = (
    store: Store,
    commodityStatus: CommodityStatus,
    category: string
  ) => {
    const result: Set<number> = new Set();
    if (isDeliveryStore(store) || isPurchaseDetailStore(store)) {
      const matchingStockEvents = (store?.stock_events || []).filter(
        (stockEvent) => stockEvent.category === category
      );

      for (const stockEvent of matchingStockEvents) {
        const stockEventId = stockEvent.id;
        if (!stockEventId) continue;
        const finalQuantity = Number(commodityStatus[stockEventId] || "0");
        const storeQuantity = Number(stockEvent.quantity);
        // If storeQuantity is larger than what we have right now, some of the
        // quantity is already used (by a consumption or incident). Deleting
        // this store would push the final quantity negative, so block it.
        if (storeQuantity > finalQuantity) {
          result.add(stockEventId);
        }
      }
    }
    return result;
  };

  // List of delivery commodities that cannot be deleted because if we delete them,
  // final quantity will be negative. They are used somewhere.
  const undeletableDeliveryCommodityIds: Set<number> = useMemo(() => {
    return getUndeletableStoreCommodities(
      currentStoreData,
      stockStatus.delivery,
      deliveryCategory
    );
  }, [currentStoreData, stockStatus.delivery]);

  // List of takehomeration commodities that cannot be deleted because if we delete them,
  // final quantity will be negative. They are used somewhere.
  const undeletableTHRCommodityIds: Set<number> = useMemo(() => {
    return getUndeletableStoreCommodities(
      currentStoreData,
      stockStatus.takehomeration,
      takeHomeRationCategory
    );
  }, [currentStoreData, stockStatus.takehomeration]);

  // List of purchase detail commodities that cannot be deleted because if we delete them,
  // if we delete them, final quantity will be negative. They are used somewhere.
  const undeletablePurchaseDetailCommodityIds: Set<number> = useMemo(() => {
    return getUndeletableStoreCommodities(
      currentStoreData,
      stockStatus.purchasedetail,
      purchaseDetailCategory
    );
  }, [currentStoreData, stockStatus.purchasedetail]);

  const isStoreUndeletable: boolean =
    undeletableTHRCommodityIds.size > 0 ||
    undeletableDeliveryCommodityIds.size > 0 ||
    undeletablePurchaseDetailCommodityIds.size > 0;

  return (
    <Modal
      open={state}
      modalHeading={
        schoolYear
          ? intl.formatMessage({
              id: "DeleteModal.schoolYearHeading",
              defaultMessage: "Delete school year",
            })
          : intl.formatMessage({
              id: "sync.deleteTitle",
              defaultMessage: "Delete",
            })
      }
      primaryButtonText={
        schoolYear
          ? intl.formatMessage({
              id: "DeleteModal.schoolYearDelete",
              defaultMessage: "Delete permanently the school year",
            })
          : intl.formatMessage({
              id: "Report.actionSubmit",
              defaultMessage: "Submit",
            })
      }
      secondaryButtonText={intl.formatMessage({
        id: "ModalExtended.cancel",
        defaultMessage: "Cancel",
      })}
      onSecondarySubmit={() => setState(false)}
      onRequestSubmit={() => onSubmit()}
      onRequestClose={() => setState(false)}
      danger={true}
      primaryButtonDisabled={isStoreUndeletable}
      // Deleting a school year wipes everything recorded in it, so open on
      // Cancel (a danger modal's tertiary button) rather than on Delete
      selectorPrimaryFocus={schoolYear ? ".wfp--btn--tertiary" : undefined}
    >
      {schoolYear && (
        <>
          <Blockquote kind="error">
            <p className={styles.irreversible}>
              <FormattedMessage
                id="DeleteModal.schoolYearIrreversible"
                defaultMessage="The deletion of the school year is irreversible."
              />
            </p>
            <p>
              <FormattedMessage
                id="DeleteModal.schoolYearConfirmation"
                defaultMessage="Are you sure you want to delete this school year? This will delete all data recorded between its start and end dates:"
              />
            </p>
            <List kind="unordered" className={styles.dataList}>
              <ListItem>
                <FormattedMessage
                  id="DeleteModal.schoolYearEnrolment"
                  defaultMessage="Enrolment"
                />
              </ListItem>
              <ListItem>
                <FormattedMessage
                  id="DeleteModal.schoolYearAttendanceAndMeals"
                  defaultMessage="Attendance and Meals"
                />
              </ListItem>
              <ListItem>
                <FormattedMessage
                  id="DeleteModal.schoolYearStock"
                  defaultMessage="Carry-over, deliveries, purchases and incidents"
                />
              </ListItem>
              <ListItem>
                <FormattedMessage
                  id="DeleteModal.schoolYearReports"
                  defaultMessage="Reports"
                />
              </ListItem>
            </List>
          </Blockquote>
          <List kind="details" colon>
            <ListItem
              title={
                <FormattedMessage
                  id="yearShow.start_date"
                  defaultMessage="Start date"
                />
              }
            >
              {moment(schoolYear.starts_on).format("dddd, DD.MM.YYYY")}
            </ListItem>
            <ListItem
              title={
                <FormattedMessage
                  id="yearShow.end_date"
                  defaultMessage="End date"
                />
              }
            >
              {moment(schoolYear.ends_on).format("dddd, DD.MM.YYYY")}
            </ListItem>
          </List>
        </>
      )}
      {!schoolYear && !isStoreUndeletable && (
        <Blockquote kind="error">
          <p>
            <FormattedMessage
              id="sync.delete"
              defaultMessage="Are you sure you want to delete this data?"
            />
          </p>
        </Blockquote>
      )}
      {isStoreUndeletable && (
        <Blockquote kind="error">
          <p>
            <FormattedMessage
              id="sync.modelInUse"
              defaultMessage="This {model} has already been used, please delete relevant consumption and incident for this commodity"
              values={{ model: getModelName(currentStoreData.model) }}
            />
          </p>
        </Blockquote>
      )}
    </Modal>
  );
};

export default DeleteModal;
